import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Search, User, Rocket, Target, ArrowLeft, Sparkles, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { PageHero, Section } from "@/components/AnalyticsCommon";
import DateInputBR from "@/components/DateInputBR";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
};

export default function OnboardingAssignPage() {
  const { companyParam } = useCompany();
  const nav = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loadingEmp, setLoadingEmp] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [chosenTemplateId, setChosenTemplateId] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoadingEmp(true);
    const params = { page_size: 1000 };
    if (companyParam) params.company_id = companyParam;
    api.get("/employees", { params })
      .then(({ data }) => setEmployees((data.items || []).filter((e) => e.status === "ativo")))
      .finally(() => setLoadingEmp(false));
  }, [companyParam]);

  const filtered = useMemo(() => {
    if (!search) return employees.slice(0, 20);
    const q = search.toLowerCase();
    return employees.filter((e) =>
      (e.nome || "").toLowerCase().includes(q) ||
      (e.matricula || "").toLowerCase().includes(q) ||
      (e.cargo || "").toLowerCase().includes(q),
    ).slice(0, 50);
  }, [employees, search]);

  const pickEmployee = async (emp) => {
    setSelected(emp);
    setLoadingSuggest(true);
    setStartDate(emp.data_admissao || new Date().toISOString().slice(0, 10));
    try {
      const { data } = await api.get(`/onboarding/suggest-template/${emp.id}`);
      setSuggestion(data);
      setChosenTemplateId(data.template?.id || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoadingSuggest(false); }
  };

  const submit = async () => {
    if (!selected || !chosenTemplateId) {
      toast.error("Selecione colaborador e trilha"); return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/onboarding/assign", {
        employee_id: selected.id,
        template_id: chosenTemplateId,
        start_date: startDate,
      });
      toast.success(`Onboarding iniciado para ${data.employee_name}.`);
      nav(`/performance/onboarding/colaborador/${selected.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 pb-10" data-testid="onboarding-assign-page">
      <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => nav(-1)}
              data-testid="onb-assign-back">
        <ArrowLeft className="h-3 w-3 mr-1" /> Voltar
      </Button>

      <PageHero
        kicker={<><Rocket className="inline h-3 w-3 mr-1.5 -mt-0.5" /> Performance & Analytics</>}
        title="Iniciar onboarding"
        description="Selecione o colaborador recém-admitido. O sistema sugere automaticamente a trilha mais adequada com base em empresa, setor, cargo e perfil."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Step 1: pick employee */}
        <Section kicker="Passo 1" title="Selecione o colaborador" testId="onb-assign-emp">
          <div className="relative mb-3">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Nome, matrícula ou cargo..." className="pl-9 h-10"
                   data-testid="onb-assign-search" />
          </div>
          {loadingEmp ? (
            <Skeleton className="h-[300px]" />
          ) : (
            <div className="border border-border rounded-md max-h-[420px] overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="p-6 text-center text-[12.5px] text-muted-foreground">Nenhum colaborador.</div>
              ) : filtered.map((emp) => (
                <button key={emp.id} type="button" onClick={() => pickEmployee(emp)}
                        className={`w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-border last:border-0 transition-colors ${selected?.id === emp.id ? "bg-[#C5A059]/10" : ""}`}
                        data-testid={`onb-emp-${emp.id}`}>
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-[#234935] text-white grid place-items-center text-[11px] font-semibold shrink-0">
                      {(emp.nome || "?").charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium truncate">{emp.nome}</div>
                      <div className="text-[10.5px] text-muted-foreground truncate">
                        Mat. {emp.matricula} · {emp.cargo} · admissão {fmtDate(emp.data_admissao)}
                      </div>
                    </div>
                    {selected?.id === emp.id && <Badge variant="outline" className="text-[9px]">Selecionado</Badge>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </Section>

        {/* Step 2: template + start date */}
        <Section kicker="Passo 2" title="Trilha e data de início" testId="onb-assign-tpl">
          {!selected ? (
            <div className="text-center py-12 text-[12.5px] text-muted-foreground">
              <User className="h-10 w-10 mx-auto text-slate-300 mb-2" />
              Escolha um colaborador para ver a sugestão de trilha.
            </div>
          ) : loadingSuggest ? (
            <Skeleton className="h-[200px]" />
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border border-[#234935]/20 bg-[#234935]/5 p-3">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-[#234935]" />
                  <div className="font-semibold text-[13px]">{selected.nome}</div>
                </div>
                <div className="text-[10.5px] text-muted-foreground mt-0.5">
                  {selected.cargo} · Mat. {selected.matricula}
                </div>
                <div className="mt-1 text-[10.5px]">
                  Perfil sugerido:{" "}
                  <Badge variant="outline" className="text-[9px] capitalize">
                    {suggestion?.suggested_profile || "—"}
                  </Badge>
                </div>
              </div>

              <div>
                <Label className="text-[11px] uppercase tracking-wider">Data de início</Label>
                <DateInputBR value={startDate} onChange={setStartDate}
                       testId="onb-assign-start" />
              </div>

              <div>
                <Label className="text-[11px] uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <Sparkles className="h-3 w-3 text-[#C5A059]" /> Trilha recomendada
                </Label>
                {suggestion?.template ? (
                  <TemplateChoice template={suggestion.template} chosen={chosenTemplateId === suggestion.template.id}
                                  onChoose={() => setChosenTemplateId(suggestion.template.id)} />
                ) : (
                  <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                    Sem trilhas ativas. Crie uma trilha primeiro em Modelos.
                  </div>
                )}
              </div>

              {(suggestion?.alternatives || []).length > 0 && (
                <div>
                  <Label className="text-[11px] uppercase tracking-wider mb-1.5">Outras trilhas</Label>
                  <div className="space-y-1.5">
                    {suggestion.alternatives.map((t) => (
                      <TemplateChoice key={t.id} template={t} chosen={chosenTemplateId === t.id}
                                      onChoose={() => setChosenTemplateId(t.id)} />
                    ))}
                  </div>
                </div>
              )}

              <Button onClick={submit} disabled={saving || !chosenTemplateId}
                      className="w-full h-11 bg-[#234935] hover:bg-[#2c5c43] text-white"
                      data-testid="onb-assign-submit">
                <Rocket className="h-3.5 w-3.5 mr-1.5" />
                {saving ? "Iniciando..." : "Iniciar onboarding"}
              </Button>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

function TemplateChoice({ template, chosen, onChoose }) {
  return (
    <button type="button" onClick={onChoose}
            className={`w-full text-left rounded-lg border p-2.5 transition-all ${chosen ? "border-[#234935] bg-[#234935]/5 ring-2 ring-[#234935]/20" : "border-border bg-white hover:border-[#234935]/40"}`}
            data-testid={`onb-tpl-choice-${template.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Target className={`h-3.5 w-3.5 ${chosen ? "text-[#234935]" : "text-[#C5A059]"}`} />
            <div className="font-medium text-[12.5px] truncate">{template.name}</div>
          </div>
          {template.descricao && (
            <p className="text-[10.5px] text-muted-foreground mt-0.5 line-clamp-2">{template.descricao}</p>
          )}
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-1">
            <span className="capitalize">{template.target_profile || "geral"}</span>
            <span className="flex items-center gap-1"><ListChecks className="h-2.5 w-2.5" /> {(template.steps || []).length} etapas</span>
            <span>· {template.duracao_dias}d</span>
          </div>
        </div>
        {chosen && <Badge variant="outline" className="text-[9px] bg-[#234935] text-white border-[#234935]">Selecionada</Badge>}
      </div>
    </button>
  );
}
